import type { BlogPost, ContentBlock } from '../../../mocks/blog';

interface ArticleBodyProps {
  post: BlogPost;
}

export default function ArticleBody({ post }: ArticleBodyProps) {
  return (
    <article className="prose-custom">
      {post.content.map((block, i) => (
        <ContentRenderer key={i} block={block} />
      ))}

      {/* External CTA */}
      {post.ctaButton && (
        <div className="my-12 rounded-2xl border border-orange-500/30 bg-gradient-to-br from-orange-500/10 via-zinc-900 to-zinc-900 p-8 text-center">
          {post.ctaButton.subtitle && (
            <p className="text-zinc-400 text-base mb-5 leading-relaxed max-w-lg mx-auto">
              {post.ctaButton.subtitle}
            </p>
          )}
          <a
            href={post.ctaButton.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-orange-500 hover:bg-orange-400 text-white font-bold text-base px-8 py-4 rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            <span>{post.ctaButton.label}</span>
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-external-link-line text-lg" />
            </div>
          </a>
          <p className="text-zinc-600 text-xs mt-4">Otvorí sa v novom okne</p>
        </div>
      )}

      {/* Tags */}
      <div className="mt-14 pt-8 border-t border-zinc-800">
        <p className="text-zinc-500 text-xs uppercase tracking-widest font-semibold mb-4">Témy článku</p>
        <div className="flex flex-wrap gap-2">
          {post.tags.map((tag) => (
            <span
              key={tag}
              className="text-sm text-zinc-300 bg-zinc-800/70 border border-zinc-700/50 rounded-full px-4 py-1.5 hover:border-orange-500/40 hover:text-orange-300 transition-all duration-200 cursor-pointer"
            >
              #{tag}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
}

function ContentRenderer({ block }: { block: ContentBlock }) {
  switch (block.type) {
    case 'lead':
      return (
        <p className="text-xl sm:text-2xl text-zinc-200 leading-relaxed font-medium mb-10 border-l-4 border-orange-500 pl-6">
          {block.text}
        </p>
      );

    case 'h2':
      return (
        <h2 className="text-2xl sm:text-3xl font-black text-white mt-12 mb-5 leading-snug">
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">
            {block.text}
          </span>
        </h2>
      );

    case 'h3':
      return (
        <h3 className="text-xl font-bold text-white mt-8 mb-4">
          {block.text}
        </h3>
      );

    case 'p':
      return (
        <p className="text-zinc-300 text-lg leading-relaxed mb-6">
          {block.text}
        </p>
      );

    case 'quote':
      return (
        <blockquote className="my-10 relative">
          <div className="bg-zinc-900 border border-zinc-700/50 border-l-4 border-l-orange-500 rounded-r-xl px-7 py-6">
            <i className="ri-double-quotes-l text-orange-400/40 text-5xl absolute -top-3 left-5" />
            <p className="text-zinc-200 text-lg italic leading-relaxed relative z-10">
              {block.text}
            </p>
          </div>
        </blockquote>
      );

    case 'list':
      return (
        <ul className="my-6 space-y-3">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-zinc-300 text-base leading-relaxed">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <div className="w-2 h-2 rounded-full bg-orange-500" />
              </div>
              {item}
            </li>
          ))}
        </ul>
      );

    case 'stats':
      return (
        <div className="my-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {block.stats.map((stat, i) => (
            <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 text-center">
              <p className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-br from-orange-400 to-amber-400 mb-1.5">
                {stat.value}
              </p>
              <p className="text-zinc-500 text-xs leading-snug">{stat.label}</p>
            </div>
          ))}
        </div>
      );

    default:
      return null;
  }
}
